/**
 * EPBCS Phase 1 — Setup: Dimension Masters, Budget Cycle, Role Matrix, User Access.
 */
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash, Sparkle, ArrowCounterClockwise, FloppyDisk, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field, Input, Select, Card, Btn } from "./common";
import DataImportTab from "./DataImportTab";
import DriverSetupTab from "./DriverSetupTab";

const DIM_CONFIG = {
  entity:         { label: "Entities",          attrs: [{ k: "base_currency", label: "Base Currency", type: "currency", required: true }] },
  division:       { label: "Divisions",         attrs: [] },
  line_of_business: { label: "Lines of Business", attrs: [] },
  department:     { label: "Departments",       attrs: [] },
  account:        { label: "Accounts",          attrs: [
    { k: "account_type", label: "Account Type", type: "select", options: ["Revenue", "Expense", "Asset", "Liability", "Equity"], required: true },
    { k: "budget_method", label: "Budget Method", type: "select", options: ["manual", "driver", "contract", "product", "other", "workforce", "capex", "material", "fuel", "fleet"] },
  ] },
  currency:       { label: "Currencies",        attrs: [{ k: "symbol", label: "Symbol", type: "text" }] },
  scenario:       { label: "Scenarios",         attrs: [] },
  version:        { label: "Versions",          attrs: [] },
  driver:         { label: "Drivers",           attrs: [
    { k: "measure", label: "Measure", type: "select", options: ["Units", "Amount", "Percentage", "Rate", "Count"] },
    { k: "formula_hint", label: "Formula Hint", type: "text" },
  ] },
  asset_category: { label: "Asset Categories",  attrs: [
    { k: "useful_life_months", label: "Useful Life (Months)", type: "number", integer: true, required: true },
    { k: "depreciation_expense_account", label: "Deprn Expense A/c", type: "text", required: true },
    { k: "asset_cost_account", label: "Asset Cost A/c", type: "text", required: true },
    { k: "accum_deprn_account", label: "Accum Deprn A/c", type: "text", required: true },
    { k: "cip_cost_account", label: "CIP Cost A/c", type: "text", required: true },
  ] },
  grade:          { label: "Grades",            attrs: [] },
  position:       { label: "Positions",         attrs: [] },
  product:        { label: "Products",          attrs: [
    { k: "type", label: "Type", type: "select", options: ["FG", "RM"], required: true },
    { k: "account_mapping", label: "Account Mapping", type: "account", required: true },
    { k: "unit", label: "Unit", type: "select", options: ["units", "kg", "liters", "count"] },
  ] },
};

const PERM_COLORS = {
  none: "bg-gray-100 text-slate-400", view: "bg-[#0B1E3F]/5 text-blue-700",
  input: "bg-green-50 text-green-700", approve: "bg-amber-50 text-amber-700",
  full: "bg-[#0B1E3F]/10 text-[#0B1E3F]",
};

export default function SetupSection({ sub }) {
  const view = ["masters", "cycle", "matrix", "users", "import", "drivers"].includes(sub) ? sub : "masters";
  const [access, setAccess] = useState(null);
  useEffect(() => { api.get("/epbcs/my-access").then((r) => setAccess(r.data)); }, []);
  const isAdmin = access?.role === "admin";

  return (
    <div className="space-y-6">
      {access && (
        <div className="flex justify-end -mb-2">
          <span className="text-[11px] text-slate-500" data-testid="epbcs-my-role">
            Your EPBCS role: <b className="uppercase text-[#0B1E3F]">{access.role.replace("_", " ")}</b>
          </span>
        </div>
      )}
      {view === "masters" && <MastersTab isAdmin={isAdmin} />}
      {view === "cycle" && <CycleTab isAdmin={isAdmin} />}
      {view === "matrix" && <MatrixTab isAdmin={isAdmin} />}
      {view === "users" && <UsersTab isAdmin={isAdmin} />}
      {view === "import" && <DataImportTab />}
      {view === "drivers" && <DriverSetupTab isAdmin={isAdmin} />}
    </div>
  );
}

// ------------------------------------------------------------ Masters ------
const MastersTab = ({ isAdmin }) => {
  const [dimType, setDimType] = useState("entity");
  const [items, setItems] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const emptyDraft = { code: "", name: "", parent_code: "", attrs: {} };
  const [draft, setDraft] = useState(emptyDraft);
  const [seeding, setSeeding] = useState(false);
  const [audit, setAudit] = useState(null);
  const cfg = DIM_CONFIG[dimType];
  const dimTypeRef = useRef(dimType);
  useEffect(() => { dimTypeRef.current = dimType; }, [dimType]);
  const load = () => {
    const reqType = dimType;
    api.get(`/epbcs/dimensions?dim_type=${reqType}`).then((r) => {
      if (reqType === dimTypeRef.current) setItems(r.data);
    });
  };
  const loadAudit = () => api.get("/epbcs/products/audit").then((r) => setAudit(r.data));
  useEffect(() => { setItems([]); load(); setDraft(emptyDraft); }, [dimType]); // eslint-disable-line
  useEffect(() => {
    if (dimType === "entity")
      api.get("/epbcs/dimensions?dim_type=currency").then((r) => setCurrencies(r.data));
    if (dimType === "product") {
      api.get("/epbcs/dimensions?dim_type=account").then((r) => setAccounts(r.data));
      loadAudit();
    }
  }, [dimType]);

  const accountOptionsFor = (type) => accounts.filter((a) =>
    type === "FG" ? a.attrs?.account_type === "Revenue" : a.attrs?.account_type === "Expense");

  const missingRequired = cfg.attrs.some((a) => a.required && !String(draft.attrs[a.k] ?? "").trim());

  const quickFix = async (issue, accountCode) => {
    const item = items.find((it) => it.code === issue.code);
    if (!item) return;
    try {
      await api.post("/epbcs/dimensions", {
        id: item.id, dim_type: "product", code: item.code, name: item.name,
        parent_code: item.parent_code || "", attrs: { ...(item.attrs || {}), account_mapping: accountCode },
      });
      toast.success(`${issue.code} re-mapped to ${accountCode}`);
      load(); loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not re-map account");
    }
  };

  const save = async () => {
    try {
      await api.post("/epbcs/dimensions", { dim_type: dimType, ...draft });
      toast.success(draft.id ? "Member updated" : "Member created");
      setDraft(emptyDraft);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save member");
    }
  };
  const startEdit = (it) => setDraft({
    id: it.id, code: it.code, name: it.name,
    parent_code: it.parent_code || "", attrs: { ...(it.attrs || {}) },
  });
  const seed = async () => {
    setSeeding(true);
    try { await api.post("/epbcs/seed-demo"); load(); } finally { setSeeding(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-1.5" data-testid="epbcs-dim-chips">
        {Object.entries(DIM_CONFIG).map(([k, c]) => (
          <button key={k} onClick={() => setDimType(k)} data-testid={`epbcs-dim-chip-${k}`}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded border transition-colors ${
              dimType === k ? "border-[#0B1E3F] text-[#0B1E3F] bg-[#0B1E3F]/5" : "border-slate-200 text-slate-600 hover:border-gray-400"
            }`}>{c.label}</button>
        ))}
        {isAdmin && (
          <button onClick={seed} disabled={seeding} data-testid="epbcs-seed-demo-btn"
            className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50">
            <Sparkle size={12} weight="fill" /> {seeding ? "Seeding…" : "Seed demo masters"}
          </button>
        )}
      </div>

      {isAdmin && (
        <Card title={draft.id ? `Edit member — ${cfg.label}` : `Add member — ${cfg.label}`} testid="epbcs-dim-editor">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label={draft.id ? "Code * (locked while editing)" : "Code *"}>
              <Input data-testid="epbcs-dim-code" value={draft.code} disabled={!!draft.id}
                className={draft.id ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""}
                title={draft.id ? "Code cannot be changed once the member is referenced elsewhere" : ""}
                onChange={(e) => setDraft({ ...draft, code: e.target.value })} /></Field>
            <Field label="Name *"><Input data-testid="epbcs-dim-name" value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></Field>
            {cfg.parentLabel && (
              <Field label={cfg.parentLabel}><Input value={draft.parent_code}
                onChange={(e) => setDraft({ ...draft, parent_code: e.target.value })} /></Field>
            )}
            {cfg.attrs.map((a) => (
              <Field key={a.k} label={a.required ? `${a.label} *` : a.label}>
                {a.type === "currency" ? (
                  <Select data-testid={`epbcs-dim-attr-${a.k}`} value={draft.attrs[a.k] || ""}
                    onChange={(e) => setDraft({ ...draft, attrs: { ...draft.attrs, [a.k]: e.target.value } })}>
                    <option value="">— select currency —</option>
                    {currencies.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                  </Select>
                ) : a.type === "account" ? (
                  <Select data-testid={`epbcs-dim-attr-${a.k}`} value={draft.attrs[a.k] || ""}
                    disabled={!draft.attrs.type}
                    onChange={(e) => setDraft({ ...draft, attrs: { ...draft.attrs, [a.k]: e.target.value } })}>
                    <option value="">{draft.attrs.type ? "— select account —" : "— select Type first —"}</option>
                    {accountOptionsFor(draft.attrs.type).map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                  </Select>
                ) : a.type === "select" ? (
                  <Select data-testid={`epbcs-dim-attr-${a.k}`} value={draft.attrs[a.k] || ""}
                    onChange={(e) => setDraft({ ...draft, attrs: { ...draft.attrs, [a.k]: e.target.value } })}>
                    <option value="">{a.required ? "— select —" : "—"}</option>
                    {a.options.map((o) => <option key={o}>{o}</option>)}
                  </Select>
                ) : (
                  <Input type={a.type} min={a.type === "number" ? "0" : undefined}
                    step={a.integer ? "1" : undefined}
                    data-testid={`epbcs-dim-attr-${a.k}`} value={draft.attrs[a.k] ?? ""}
                    onChange={(e) => setDraft({ ...draft, attrs: { ...draft.attrs, [a.k]:
                      a.type === "number"
                        ? (a.integer ? Math.max(0, Math.floor(+e.target.value || 0)) : Math.max(0, +e.target.value))
                        : e.target.value } })} />
                )}
              </Field>
            ))}
          </div>
          <div className="mt-3 flex justify-end gap-2">
            {draft.id && (
              <button onClick={() => setDraft(emptyDraft)} data-testid="epbcs-dim-cancel"
                className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">
                Cancel
              </button>
            )}
            <Btn onClick={save} disabled={!draft.code || !draft.name || missingRequired} data-testid="epbcs-dim-save">
              <Plus size={12} className="inline mr-1" />{draft.id ? "Update member" : "Save member"}
            </Btn>
          </div>
        </Card>
      )}

      <Card title={`${cfg.label} (${items.length})`} testid="epbcs-dim-list">
        {dimType === "product" && audit && audit.issues.length > 0 && (
          <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3" data-testid="product-audit-banner">
            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-800">
              Account Mapping Audit — {audit.issues.length} of {audit.checked} product(s) need attention
            </p>
            <p className="mt-1 text-[11px] text-amber-700">
              These products' Total Cost/Revenue row will not appear on the budget grids until fixed.
            </p>
            <div className="mt-2 space-y-1.5">
              {audit.issues.map((iss) => (
                <div key={iss.code} className="flex flex-wrap items-center gap-2 text-[12px]"
                  data-testid={`product-audit-issue-${iss.code}`}>
                  <span className="font-mono font-semibold text-amber-900">{iss.code}</span>
                  <span className="text-amber-800">{iss.name}</span>
                  <span className="text-amber-600">— {iss.detail}</span>
                  {isAdmin && (
                    <Select data-testid={`product-audit-fix-${iss.code}`} className="ml-auto w-56 text-[11px]"
                      value="" onChange={(e) => e.target.value && quickFix(iss, e.target.value)}>
                      <option value="">— re-map to account —</option>
                      {accountOptionsFor(iss.type).map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                    </Select>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {items.length === 0 ? (
          <p className="text-sm text-slate-400">No members yet.{isAdmin ? " Add one above or seed demo masters." : ""}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Code</th><th className="py-2 pr-3">Name</th>
                  {cfg.parentLabel && <th className="py-2 pr-3">{cfg.parentLabel}</th>}
                  {cfg.attrs.map((a) => <th key={a.k} className="py-2 pr-3">{a.label}</th>)}
                  {isAdmin && <th className="py-2 w-8" />}
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`epbcs-dim-row-${it.code}`}>
                    <td className="py-2 pr-3 font-mono text-[12px]">{it.code}</td>
                    <td className="py-2 pr-3 font-medium">{it.name}</td>
                    {cfg.parentLabel && <td className="py-2 pr-3 text-slate-500 font-mono text-[12px]">{it.parent_code || "—"}</td>}
                    {cfg.attrs.map((a) => <td key={a.k} className="py-2 pr-3 text-slate-600 text-[12px]">{it.attrs?.[a.k] ?? "—"}</td>)}
                    {isAdmin && (
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <button onClick={() => startEdit(it)}
                            data-testid={`epbcs-dim-edit-${it.code}`}
                            className="text-slate-400 hover:text-[#0B1E3F]"><PencilSimple size={14} /></button>
                          <button onClick={async () => {
                              try { await api.delete(`/epbcs/dimensions/${it.id}`); load(); }
                              catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
                            }}
                            data-testid={`epbcs-dim-delete-${it.code}`}
                            className="text-slate-400 hover:text-red-600"><Trash size={14} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

// ------------------------------------------------------- Budget cycle ------
const CycleTab = ({ isAdmin }) => {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  useEffect(() => {
    api.get("/epbcs/settings").then((r) => setS(r.data));
    api.get("/epbcs/dimensions?dim_type=currency").then((r) => setCurrencies(r.data));
  }, []);
  if (!s) return <p className="text-sm text-slate-400">Loading…</p>;
  const save = async () => {
    const { data } = await api.post("/epbcs/settings", s);
    setS(data); setSaved(true); setTimeout(() => setSaved(false), 2000);
  };
  const upd = (k) => (e) => setS({ ...s, [k]: e.target.value });
  return (
    <div className="space-y-5">
    <Card title="Budget Cycle Configuration"
      subtitle="Months up to the “actuals through” period are populated from actual data; the remaining months are open for planning input."
      testid="epbcs-cycle-card">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl">
        <Field label="Current Year"><Input data-testid="epbcs-cycle-current-year" value={s.current_year} onChange={upd("current_year")} /></Field>
        <Field label="Base Currency">
          <Select data-testid="epbcs-cycle-base-currency" value={s.base_currency || ""} onChange={upd("base_currency")}>
            <option value="">— select currency —</option>
            {currencies.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
          </Select>
        </Field>
        <Field label="Actuals Through (YYYY-MM)"><Input value={s.actuals_through} onChange={upd("actuals_through")} /></Field>
        <Field label="Budget Start (YYYY-MM)"><Input value={s.budget_start} onChange={upd("budget_start")} /></Field>
        <Field label="Budget End (YYYY-MM)"><Input value={s.budget_end} onChange={upd("budget_end")} /></Field>
      </div>
      {isAdmin && (
        <div className="mt-4 flex items-center gap-3">
          <Btn onClick={save} data-testid="epbcs-cycle-save"><FloppyDisk size={12} className="inline mr-1" />Save configuration</Btn>
          {saved && <span className="text-[12px] font-semibold text-green-700">Saved ✓</span>}
        </div>
      )}
    </Card>
    <ActualsCard isAdmin={isAdmin} />
    </div>
  );
};

// ------------------------------------------------------ Actuals sources ----
const ActualsCard = ({ isAdmin }) => {
  const [status, setStatus] = useState(null);
  const [entities, setEntities] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [entity, setEntity] = useState("");
  const [ledgerId, setLedgerId] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/epbcs/actuals/status").then((r) => setStatus(r.data));
  useEffect(() => {
    load();
    api.get("/epbcs/dimensions?dim_type=entity").then((r) => {
      setEntities(r.data);
      if (r.data[0]) setEntity(r.data[0].code);
    });
    api.get("/epm/ledgers").then((r) => setLedgers(r.data || [])).catch(() => setLedgers([]));
  }, []);

  const sync = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/epbcs/actuals/sync-tb",
        { entity, ...(ledgerId ? { ledger_id: ledgerId } : {}) });
      setResult(data);
      load();
    } catch (e) {
      setResult({ ok: false, message: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <Card title="Actuals Sources"
      subtitle="Actual months of every budgeting grid are populated from these datapoints. Sync real actuals from your Oracle Fusion Trial Balance pulls (EPM → Trial Balance), or use demo data."
      testid="epbcs-actuals-card">
      {status && status.length > 0 ? (
        <table className="w-full text-sm mb-4">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Entity</th><th className="py-2 pr-3">Source</th>
              <th className="py-2 pr-3">Accounts</th><th className="py-2 pr-3">Datapoints</th>
              <th className="py-2 pr-3">Coverage</th>
            </tr>
          </thead>
          <tbody>
            {status.map((r, i) => (
              <tr key={i} className="border-b border-slate-100" data-testid={`actuals-status-${r.entity}-${r.source}`}>
                <td className="py-2 pr-3 font-mono text-[12px]">{r.entity}</td>
                <td className="py-2 pr-3">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${
                    r.source === "oracle_tb" ? "bg-green-50 text-green-700 border border-green-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                    {r.source === "oracle_tb" ? "Oracle TB" : "Demo"}
                  </span>
                </td>
                <td className="py-2 pr-3">{r.accounts}</td>
                <td className="py-2 pr-3">{r.datapoints}</td>
                <td className="py-2 pr-3 font-mono text-[12px]">{r.from_month} → {r.to_month}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-slate-400 mb-4" data-testid="actuals-status-empty">No actuals loaded yet.</p>
      )}
      {isAdmin && (
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Entity">
            <Select value={entity} data-testid="actuals-sync-entity" onChange={(e) => setEntity(e.target.value)}>
              {entities.map((en) => <option key={en.code} value={en.code}>{en.code} — {en.name}</option>)}
            </Select>
          </Field>
          <Field label="Ledger (optional)">
            <Select value={ledgerId} data-testid="actuals-sync-ledger" onChange={(e) => setLedgerId(e.target.value)}>
              <option value="">All ledgers</option>
              {ledgers.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Select>
          </Field>
          <Btn onClick={sync} disabled={busy || !entity} data-testid="actuals-sync-btn">
            {busy ? "Syncing…" : "Sync from Oracle Trial Balance"}
          </Btn>
        </div>
      )}
      {result && (
        <div className={`mt-3 rounded-lg border px-3 py-2 text-[12px] ${result.ok ? "border-green-200 bg-green-50 text-green-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}
          data-testid="actuals-sync-result">
          <b>{result.ok ? "Sync complete." : "Nothing synced."}</b> {result.message}
          {result.ok && result.periods?.length > 0 && (
            <div className="mt-1 text-[11px]">
              Periods: {result.periods.join(", ")} · Accounts matched: {result.accounts_matched.length}
              {result.unmatched_tb_accounts?.length > 0 && (
                <> · TB accounts without an EPBCS master (skipped): {result.unmatched_tb_accounts.join(", ")}</>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// -------------------------------------------------------- Role matrix ------
const MatrixTab = ({ isAdmin }) => {
  const [forms, setForms] = useState([]);
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [saved, setSaved] = useState(false);
  const load = () => Promise.all([api.get("/epbcs/forms"), api.get("/epbcs/role-matrix")])
    .then(([f, m]) => { setForms(f.data.forms); setRoles(f.data.roles); setPerms(f.data.permissions); setMatrix(m.data.matrix); });
  useEffect(() => { load(); }, []);
  if (!matrix) return <p className="text-sm text-slate-400">Loading…</p>;

  const save = async () => {
    await api.post("/epbcs/role-matrix", { matrix });
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };
  const reset = async () => {
    const { data } = await api.post("/epbcs/role-matrix/reset");
    setMatrix(data.matrix);
  };
  const phases = [...new Set(forms.map((f) => f.phase))].sort();

  return (
    <Card title="Form-to-Role Access Matrix"
      subtitle="Controls what each EPBCS role can do on every form. Platform admins & product admins are always EPBCS admins."
      testid="epbcs-matrix-card"
      actions={isAdmin && (
        <div className="flex gap-2">
          <button onClick={reset} data-testid="epbcs-matrix-reset"
            className="inline-flex items-center gap-1 border border-slate-300 text-slate-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded hover:border-gray-500">
            <ArrowCounterClockwise size={12} /> Reset defaults
          </button>
          <Btn onClick={save} data-testid="epbcs-matrix-save"><FloppyDisk size={12} className="inline mr-1" />Save matrix</Btn>
        </div>
      )}>
      {saved && <div className="mb-2 text-[12px] font-semibold text-green-700">Matrix saved ✓</div>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Form</th>
              {roles.map((r) => <th key={r} className="py-2 px-2 text-center">{r.replace("_", " ")}</th>)}
            </tr>
          </thead>
          <tbody>
            {phases.map((p) => (
              <React.Fragment key={p}>
                <tr><td colSpan={roles.length + 1} className="pt-3 pb-1 text-[10px] tracking-[0.2em] uppercase font-bold text-amber-700">Phase {p}</td></tr>
                {forms.filter((f) => f.phase === p).map((f) => (
                  <tr key={f.key} className="border-b border-slate-100" data-testid={`epbcs-matrix-row-${f.key}`}>
                    <td className="py-1.5 pr-3 font-medium text-[13px]">{f.label}</td>
                    {roles.map((r) => (
                      <td key={r} className="py-1.5 px-2 text-center">
                        {isAdmin ? (
                          <select value={matrix[r]?.[f.key] || "none"} data-testid={`epbcs-matrix-${r}-${f.key}`}
                            onChange={(e) => setMatrix({ ...matrix, [r]: { ...matrix[r], [f.key]: e.target.value } })}
                            className={`text-[11px] font-semibold rounded px-1.5 py-1 border-0 ${PERM_COLORS[matrix[r]?.[f.key] || "none"]}`}>
                            {perms.map((pm) => <option key={pm} value={pm}>{pm}</option>)}
                          </select>
                        ) : (
                          <span className={`inline-block text-[11px] font-semibold rounded px-2 py-0.5 ${PERM_COLORS[matrix[r]?.[f.key] || "none"]}`}>
                            {matrix[r]?.[f.key] || "none"}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// --------------------------------------------------------- User access -----
const UsersTab = ({ isAdmin }) => {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/epbcs/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const assign = async (email, role) => {
    await api.post("/epbcs/user-roles", { email, epbcs_role: role });
    load();
  };
  return (
    <Card title="User Access"
      subtitle="Assign an EPBCS role to each user in your organisation. Users without an assignment default to Viewer."
      testid="epbcs-users-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-slate-500 border-b border-slate-200">
            <th className="py-2 pr-3">User</th><th className="py-2 pr-3">Platform Role</th><th className="py-2 pr-3">EPBCS Role</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.email} className="border-b border-slate-100" data-testid={`epbcs-user-row-${u.email}`}>
              <td className="py-2 pr-3 font-medium">{u.email}</td>
              <td className="py-2 pr-3 text-slate-500 text-[12px] uppercase">{u.role || "user"}</td>
              <td className="py-2 pr-3">
                {u.is_platform_admin ? (
                  <span className="text-[11px] font-bold uppercase text-[#0B1E3F]">Admin (always)</span>
                ) : isAdmin ? (
                  <select value={u.epbcs_role} data-testid={`epbcs-user-role-${u.email}`}
                    onChange={(e) => assign(u.email, e.target.value)}
                    className="border border-slate-300 px-2 py-1 text-[12px] bg-white rounded">
                    {["admin", "preparer", "alt_preparer", "reviewer", "viewer"].map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
                  </select>
                ) : (
                  <span className="text-[12px] uppercase font-semibold">{u.epbcs_role.replace("_", " ")}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
};
